import {
  abbreviateNumber,
  americanDateFormat,
  centsToDollars,
  dollarsToCents,
  formatDollarsFromCents,
  Maybe,
} from '@common/neo.tax';
import { Alert, Badge, Checkbox, Stack, Typography, useModal } from '@ui-components/neo.tax';
import { ReactComponent as PayrollIcon } from '@ui-components/neo.tax/dist/icons/single-neutral.svg';
import compact from 'lodash/compact';
import uniqueId from 'lodash/uniqueId';
import { ReactElement, useMemo, useState } from 'react';
import { CellProps, Column, type UseRowSelectInstanceProps } from 'react-table';

import { SRED_CATEGORY_LABELS } from '@neo.tax/ca/rd/constants';
import { SredPayrollExpense, useSredPayrollExpenses } from '@neo.tax/ca/rd/hooks/useSredPayrollExpenses';
import {
  SredPayrollExpensesSummary,
  useSredPayrollExpensesSummary,
} from '@neo.tax/ca/rd/hooks/useSredPayrollExpensesSummary';
import {
  FilterSortDataType,
  PAYROLL_PROVIDER_NAMES,
  PercentCell,
  ProviderType,
  SortDirection,
  SourcesContent,
  SredPayrollExpenseSortFields,
  TABLE_CELL_PLACEHOLDER,
  TableHeader,
  useBackgroundOperations,
  useColumnVisibility,
  useCurrentUserScopeForCurrentCompany,
  useFilterConditions,
  useKeywordSearch,
  useSorting,
  type ColumnDefinition,
  type ProviderUnion,
  type SyncedProviders,
} from '@neo.tax/shared';
import {
  ExpenseLayout,
  FooterCell,
  TypographyFullWidth,
  UploadExpenseProjectAllocationReportStatus,
} from '@neo.tax/shared/organisms';
import { useSredContext } from '@neo.tax/sred/context';
import { SredLockPageLayout } from '@neo.tax/sred/organisms/sred-lock-page-layout';

import { SredPayrollExpenseNameCell } from './SredPayrollExpenseNameCell';
import { useSredPayrollExpenseLayoutAnalyticIds } from './useSredPayrollExpenseLayoutAnalyticIds';

type SredPayrollExpenseCell = CellProps<SredPayrollExpense, Maybe<string>> &
  UseRowSelectInstanceProps<SredPayrollExpense>;

const sredPayrollExpenseTableColumns =
  (
    footerData: SredPayrollExpensesSummary | null,
    isPiiRedacted: boolean,
    limitAccessToFinancialData: boolean,
    modalOptions: {
      toggle: () => void;
      setSelectedSredExpenseId: (sredExpenseId: string) => void;
    },
    visibleColumns: SredPayrollExpenseSortFields[],
  ) =>
  ({
    handleClickRow,
    handleSelectAllRows,
    handleSelectRow,
    rowCount,
    selectedRowIds,
    syncedProviders,
  }: {
    handleClickRow: (cell: SredPayrollExpenseCell) => () => void;
    handleSelectAllRows: (checked: boolean) => void;
    handleSelectRow: (cell: SredPayrollExpenseCell) => (checked: boolean) => void;
    rowCount: number;
    selectedRowIds: string[];
    syncedProviders?: SyncedProviders;
  }): Column<SredPayrollExpense>[] => {
    const amountCentsColumns: Column<SredPayrollExpense>[] = limitAccessToFinancialData
      ? []
      : [
          {
            id: SredPayrollExpenseSortFields.QualifiedAmountCents,
            accessor: 'qualifiedAmountCents',
            width: 145,
            Header: () => <TableHeader textAlign="right">Qualified $</TableHeader>,
            Cell: ({
              value,
            }: CellProps<SredPayrollExpense, SredPayrollExpense['qualifiedAmountCents']>): ReactElement => (
              <TypographyFullWidth as="p" color="inherit" fontSize="inherit" textAlign="right">
                {value != null ? formatDollarsFromCents(value, { showCents: false }) : TABLE_CELL_PLACEHOLDER}
              </TypographyFullWidth>
            ),
            Footer: (): ReactElement => {
              const hasData = (footerData?.sum?.qualifiedAmountCents ?? null) !== null;
              const abbreviatedValue = abbreviateNumber({
                inputNumber: centsToDollars(footerData?.sum?.qualifiedAmountCents ?? 0),
              }).abbreviation;
              const value = hasData ? `$${abbreviatedValue}` : '0';
              return <FooterCell preText="TOTAL" text={value} alignText="end" />;
            },
          },
          {
            id: SredPayrollExpenseSortFields.AmountCents,
            accessor: 'amountCents',
            width: 145,
            Header: () => <TableHeader textAlign="right">Amount $</TableHeader>,
            Cell: ({ value }: CellProps<SredPayrollExpense, SredPayrollExpense['amountCents']>): ReactElement => (
              <TypographyFullWidth as="p" color="inherit" fontSize="inherit" textAlign="right">
                {value != null ? formatDollarsFromCents(value, { showCents: false }) : TABLE_CELL_PLACEHOLDER}
              </TypographyFullWidth>
            ),
            Footer: (): ReactElement => {
              const hasData = (footerData?.sum?.amountCents ?? null) !== null;
              const abbreviatedValue = abbreviateNumber({
                inputNumber: centsToDollars(footerData?.sum?.amountCents ?? 0),
              }).abbreviation;
              const value = hasData ? `$${abbreviatedValue}` : '0';
              return <FooterCell preText="TOTAL" text={value} alignText="end" />;
            },
          },
        ];

    const columns: Column<SredPayrollExpense>[] = [
      {
        id: SredPayrollExpenseSortFields.Name,
        accessor: 'name',
        width: 190,
        Header: () => (
          <Stack direction="row">
            <Checkbox
              id="expenseIds"
              name="expenseIds"
              checked={selectedRowIds.length > 0 && selectedRowIds.length === rowCount}
              onChange={handleSelectAllRows}
            />
            <TableHeader>Name</TableHeader>
          </Stack>
        ),
        Cell: (
          cell: CellProps<SredPayrollExpense, SredPayrollExpense['name']> &
            UseRowSelectInstanceProps<SredPayrollExpense>,
        ): ReactElement => (
          <Stack direction="row">
            <Checkbox
              id={cell.row.original.id}
              name={cell.row.original.id}
              checked={selectedRowIds?.includes(cell.row.original.id)}
              onChange={handleSelectRow(cell)}
            />

            <SredPayrollExpenseNameCell
              name={cell.value}
              hasSredPayrollExpense={!!cell.row.original.sredPayrollExpenseId}
              isPiiRedacted={isPiiRedacted}
              onClick={handleClickRow(cell)}
            />
          </Stack>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.count?.name ?? '0';
          return <FooterCell preText="COUNT" text={value} />;
        },
      },
      {
        id: SredPayrollExpenseSortFields.Title,
        accessor: 'title',
        width: 190,
        Header: () => <TableHeader>Title</TableHeader>,
        Cell: ({ value }: CellProps<SredPayrollExpense, SredPayrollExpense['title']>): ReactElement => (
          <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
            {value || TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.title ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
      {
        id: SredPayrollExpenseSortFields.Department,
        accessor: 'department',
        width: 190,
        Header: () => <TableHeader>Department</TableHeader>,
        Cell: ({ value }: CellProps<SredPayrollExpense, SredPayrollExpense['department']>): ReactElement => (
          <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
            {value || TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.department ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
      {
        id: SredPayrollExpenseSortFields.ManagerName,
        accessor: 'managerName',
        width: 190,
        Header: () => <TableHeader>Manager</TableHeader>,
        Cell: ({ value }: CellProps<SredPayrollExpense, SredPayrollExpense['managerName']>): ReactElement => (
          <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
            {value || TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.managerName ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
      {
        id: SredPayrollExpenseSortFields.HireDate,
        accessor: 'hireDate',
        width: 120,
        Header: () => <TableHeader>Hire Date</TableHeader>,
        Cell: ({ value }: CellProps<SredPayrollExpense, SredPayrollExpense['hireDate']>): ReactElement => (
          <Typography color="inherit" fontSize="inherit">
            {value ? americanDateFormat(value, { zeroPadded: false }) : TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
      },
      {
        id: SredPayrollExpenseSortFields.TerminationDate,
        accessor: 'terminationDate',
        width: 120,
        Header: () => <TableHeader>Term. Date</TableHeader>,
        Cell: ({ value }: CellProps<SredPayrollExpense, SredPayrollExpense['terminationDate']>): ReactElement => (
          <Typography color="inherit" fontSize="inherit">
            {value ? americanDateFormat(value, { zeroPadded: false }) : TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
      },
      {
        id: SredPayrollExpenseSortFields.CostCenter,
        accessor: 'costCenter',
        width: 190,
        Header: () => <TableHeader>Cost Center</TableHeader>,
        Cell: ({ value }: CellProps<SredPayrollExpense, SredPayrollExpense['costCenter']>): ReactElement => (
          <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
            {value || TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.costCenter ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
      {
        id: SredPayrollExpenseSortFields.Entity,
        accessor: 'entity',
        width: 190,
        Header: () => <TableHeader>Entity</TableHeader>,
        Cell: ({ value }: CellProps<SredPayrollExpense, SredPayrollExpense['entity']>): ReactElement => (
          <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
            {value || TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.entity ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
      ...amountCentsColumns,
      {
        id: SredPayrollExpenseSortFields.SredEligiblePercentage,
        accessor: 'sredEligiblePercentage',
        width: 105,
        Header: () => <TableHeader textAlign="right">SR&ED %</TableHeader>,
        Cell: ({
          row,
          value,
        }: CellProps<SredPayrollExpense, SredPayrollExpense['sredEligiblePercentage']>): ReactElement => (
          <PercentCell
            onClick={(): void => {
              modalOptions.setSelectedSredExpenseId(row.original.id);
              modalOptions.toggle();
            }}
            percent={value}
            isSelected={row.isSelected}
          />
        ),
        Footer: (): ReactElement => {
          const hasData = (footerData?.avg?.sredEligiblePercentage ?? null) !== null;
          const value = hasData ? `${Math.round(footerData?.avg?.sredEligiblePercentage ?? 0)}%` : '0';
          return <FooterCell preText="AVG" text={value} alignText="end" />;
        },
      },
      {
        id: SredPayrollExpenseSortFields.PayrollProvider,
        accessor: (datum: SredPayrollExpense): ProviderUnion[] =>
          compact([datum.payrollProvider, ...datum.ticketingProviders]),
        Cell: ({ value }: CellProps<SredPayrollExpense, ProviderUnion[]>): ReactElement => (
          <SourcesContent linkedProviders={value} allProviders={syncedProviders} placeholder={TABLE_CELL_PLACEHOLDER} />
        ),
        Header: () => <TableHeader>Sources</TableHeader>,
      },
      {
        id: SredPayrollExpenseSortFields.Province,
        accessor: (datum: SredPayrollExpense): string | null | undefined => datum.province,
        width: 110,
        Header: () => <TableHeader>Province</TableHeader>,
        Cell: ({ value }: CellProps<SredPayrollExpense, string | null | undefined>): string =>
          value || TABLE_CELL_PLACEHOLDER,
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.province ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
      {
        id: SredPayrollExpenseSortFields.SredCategory,
        accessor: 'sredCategory',
        width: 'auto',
        Header: () => <TableHeader>SR&ED Category</TableHeader>,
        Cell: ({ value }: CellProps<SredPayrollExpense, SredPayrollExpense['sredCategory']>): ReactElement => (
          <Badge colorVariant="green" icon={<PayrollIcon />}>
            {SRED_CATEGORY_LABELS[value]}
          </Badge>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.sredCategory ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
    ];

    return columns.filter((column) => visibleColumns.includes(column.id as SredPayrollExpenseSortFields));
  };

const sortAndFilterableColumnsForNonFinancialUser = [
  { name: SredPayrollExpenseSortFields.Name, label: 'Name', dataType: FilterSortDataType.TEXT },
  { name: SredPayrollExpenseSortFields.Title, label: 'Title', dataType: FilterSortDataType.TEXT },
  { name: SredPayrollExpenseSortFields.Department, label: 'Department', dataType: FilterSortDataType.TEXT },
  { name: SredPayrollExpenseSortFields.ManagerName, label: 'Manager', dataType: FilterSortDataType.TEXT },
  { name: SredPayrollExpenseSortFields.HireDate, label: 'Hire Date', dataType: FilterSortDataType.DATE },
  { name: SredPayrollExpenseSortFields.TerminationDate, label: 'Termination Date', dataType: FilterSortDataType.DATE },
  { name: SredPayrollExpenseSortFields.CostCenter, label: 'Cost Center', dataType: FilterSortDataType.TEXT },
  { name: SredPayrollExpenseSortFields.Entity, label: 'Entity', dataType: FilterSortDataType.TEXT },
  {
    name: SredPayrollExpenseSortFields.SredEligiblePercentage,
    label: 'SR&ED %',
    dataType: FilterSortDataType.NUMBER,
  },
  {
    name: SredPayrollExpenseSortFields.PayrollProvider,
    label: 'Source',
    dataType: FilterSortDataType.ENUM,
    enum: Object.entries(PAYROLL_PROVIDER_NAMES).map(([value, label]) => ({ value, label })),
  },
  { name: SredPayrollExpenseSortFields.Province, label: 'Province', dataType: FilterSortDataType.TEXT },
  {
    name: SredPayrollExpenseSortFields.SredCategory,
    label: 'SR&ED Category',
    dataType: FilterSortDataType.ENUM,
    enum: Object.entries(SRED_CATEGORY_LABELS).map(([value, label]) => ({ value, label })),
  },
] as const satisfies readonly ColumnDefinition<SredPayrollExpenseSortFields>[];

const indexOfEntityColumn = sortAndFilterableColumnsForNonFinancialUser.findIndex(
  (column) => column.name === SredPayrollExpenseSortFields.Entity,
);
const sortAndFilterableColumnsForFullFinancialUser = [
  ...sortAndFilterableColumnsForNonFinancialUser.slice(0, indexOfEntityColumn + 1),
  {
    name: SredPayrollExpenseSortFields.QualifiedAmountCents,
    label: 'Qualified $',
    dataType: FilterSortDataType.NUMBER,
    toHuman: (n): number | null => (n === null ? null : centsToDollars(Number(n))),
    fromHuman: (n): number | null => (n === null ? null : dollarsToCents(Number(n))),
  },
  {
    name: SredPayrollExpenseSortFields.AmountCents,
    label: 'Amount $',
    dataType: FilterSortDataType.NUMBER,
    toHuman: (n): number | null => (n === null ? null : centsToDollars(Number(n))),
    fromHuman: (n): number | null => (n === null ? null : dollarsToCents(Number(n))),
  },
  ...sortAndFilterableColumnsForNonFinancialUser.slice(indexOfEntityColumn + 1),
] as const satisfies readonly ColumnDefinition<SredPayrollExpenseSortFields>[];
const sortAndFilterableColumnsForPiiRedactedUser = [...sortAndFilterableColumnsForFullFinancialUser.slice(1)];

export const SredPayrollExpenseLayout = (): ReactElement => {
  const { fiscalYear, fiscalYearId, isNowPastPeriodEndDate, sredTaxCreditId } = useSredContext();
  const { toggle } = useModal();
  const [selectedSredExpenseId, setSelectedSredExpenseId] = useState<string | undefined>(undefined);
  const { isPiiRedacted, limitAccessToFinancialData } = useCurrentUserScopeForCurrentCompany();
  const analyticIds = useSredPayrollExpenseLayoutAnalyticIds();
  const searchKeywordState = useKeywordSearch();

  // TODO: Implement SR&ED expense project allocation report download/upload when backend support is added
  const downloadBulkEditTemplate = async (): Promise<void> => {
    // No-op until backend implements getSredExpenseProjectAllocationReport query
  };
  const isDownloadingBulkEditTemplate = false;
  const bulkEditUpload = {
    status: UploadExpenseProjectAllocationReportStatus.NOT_STARTED,
    errors: null,
    upload: async (): Promise<void> => {
      // No-op until backend implements uploadSredExpenseProjectAllocationReport mutation
    },
    reset: (): void => {
      // No-op
    },
  };

  // Poll for background operations to evict company cache when SR&ED cost file import completes
  useBackgroundOperations();

  const sortAndFilterableColumns = limitAccessToFinancialData
    ? sortAndFilterableColumnsForNonFinancialUser
    : isPiiRedacted
      ? sortAndFilterableColumnsForPiiRedactedUser
      : sortAndFilterableColumnsForFullFinancialUser;
  const initialSorting = [
    {
      id: uniqueId('SredPayrollExpenseSortField'),
      column: SredPayrollExpenseSortFields.SredEligiblePercentage,
      direction: SortDirection.Desc,
    },
  ];
  if (!limitAccessToFinancialData) {
    initialSorting.push({
      id: uniqueId('SredPayrollExpenseSortField'),
      column: SredPayrollExpenseSortFields.AmountCents,
      direction: SortDirection.Desc,
    });
  }

  const filterState = useFilterConditions({ columns: sortAndFilterableColumns, persistenceKey: 'sredPayrollExpense' });
  const sortState = useSorting({
    columns: sortAndFilterableColumns,
    initialSorting,
    persistenceKey: 'sredPayrollExpense',
  });
  const columnVisibilityState = useColumnVisibility({
    columns: sortAndFilterableColumns,
    alwaysVisibleColumns: [SredPayrollExpenseSortFields.Name, SredPayrollExpenseSortFields.SredEligiblePercentage],
    initialVisibleColumns: [
      SredPayrollExpenseSortFields.Title,
      SredPayrollExpenseSortFields.Department,
      SredPayrollExpenseSortFields.ManagerName,
      SredPayrollExpenseSortFields.PayrollProvider,
      SredPayrollExpenseSortFields.Province,
      SredPayrollExpenseSortFields.SredCategory,
      ...(limitAccessToFinancialData
        ? []
        : [SredPayrollExpenseSortFields.QualifiedAmountCents, SredPayrollExpenseSortFields.AmountCents]),
    ],
    persistenceKey: 'sredPayrollExpense',
  });

  // memo inputs
  const sorting = useMemo(
    () => sortState.sorts.map((s) => ({ field: s.column, direction: s.direction })),
    [sortState.sorts],
  );

  const { isLoadingMore, loading, onLoadMoreItems, sredPayrollExpenses } = useSredPayrollExpenses({
    sredTaxCreditId,
    searchKeyword: searchKeywordState.searchKeyword,
    filterConditions: filterState.conditions,
    sorting,
  });
  const { loading: loadingSummary, sredPayrollExpensesSummary } = useSredPayrollExpensesSummary({
    sredTaxCreditId,
    filterConditions: filterState.conditions,
    searchKeyword: searchKeywordState.searchKeyword,
  });

  const hasSomeContributors = sredPayrollExpenses?.some((row) => row.sredContributorId && !row.sredPayrollExpenseId);

  return (
    <>
      <ExpenseLayout
        pageTitle="SR&ED Payroll Expenses"
        pageSubtitle="Click on a team member to review and modify details."
        connectableProviderTypes={new Set([ProviderType.Payroll])}
        connectProviderLabel="Connect Payroll"
        bulkEditProviderType={ProviderType.Payroll}
        searchKeywordState={searchKeywordState}
        filterState={filterState}
        sortState={sortState}
        columnVisibilityState={columnVisibilityState}
        createColumns={sredPayrollExpenseTableColumns(
          sredPayrollExpensesSummary,
          isPiiRedacted,
          limitAccessToFinancialData,
          {
            toggle,
            setSelectedSredExpenseId,
          },
          columnVisibilityState.visibleColumns,
        )}
        tableRows={sredPayrollExpenses}
        loadingRows={loading}
        loadingFooter={loadingSummary || isLoadingMore}
        isPiiRedacted={isPiiRedacted}
        onLoadMoreItems={onLoadMoreItems}
        ExpenseDetail={(): ReactElement => <div>SR&ED Payroll Expense Detail - Coming Soon</div>}
        LockPageLayout={SredLockPageLayout}
        fiscalYear={fiscalYear}
        fiscalYearId={fiscalYearId}
        isNowPastPeriodEndDate={isNowPastPeriodEndDate}
        analyticIds={analyticIds}
        bulkEditTemplate={{
          onDownload: downloadBulkEditTemplate,
          isDownloading: isDownloadingBulkEditTemplate,
        }}
        bulkEditUpload={bulkEditUpload}
        alert={
          hasSomeContributors && (
            <Alert
              severity="warning"
              title="Team members with a yellow alert icon have not been linked to payroll data. Please connect your payroll system."
            />
          )
        }
      />
      {/* TODO: Implement SR&ED-specific allocation modal */}
      {selectedSredExpenseId && <div>SR&ED Expense Allocation Modal - Coming Soon (ID: {selectedSredExpenseId})</div>}
    </>
  );
};
